/** Browser-saved Supabase project. Empty fields mean "use the build default". */

export interface SupabaseConfig {
  url: string;
  /** Publishable key, or the legacy anon JWT. Never a secret / service_role key. */
  publishableKey: string;
}

export const EMPTY_SUPABASE: SupabaseConfig = { url: '', publishableKey: '' };

const STORAGE_KEY = 'orbomb.supabase.v1';

export function sanitizeSupabaseConfig(value: unknown): SupabaseConfig {
  const raw = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  const url = typeof raw.url === 'string' ? projectUrl(raw.url) : '';
  return { url, publishableKey: publicKey(raw) };
}

function publicKey(raw: Record<string, unknown>): string {
  for (const field of ['publishableKey', 'anonKey'] as const) {
    const value = raw[field];
    if (typeof value !== 'string') continue;
    const key = value.trim();
    if (isClientSupabaseKey(key)) return key;
  }
  return '';
}

/** True for a publishable key or a legacy anon/authenticated JWT. Never secret / service_role. */
export function isClientSupabaseKey(value: string): boolean {
  const key = value.trim();
  if (!key) return false;
  if (key.startsWith('sb_secret_')) return false;
  if (key.startsWith('sb_publishable_')) return true;
  const role = jwtRole(key);
  return role === 'anon' || role === 'authenticated';
}

function jwtRole(token: string): string | null {
  const parts = token.split('.');
  const payload = parts[1];
  if (!payload) return null;
  try {
    const padded = payload.replace(/-/g, '+').replace(/_/g, '/');
    const extra = '='.repeat((4 - (padded.length % 4)) % 4);
    const parsed: unknown = JSON.parse(atob(padded + extra));
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    const role = (parsed as { role?: unknown }).role;
    return typeof role === 'string' ? role : null;
  } catch {
    return null;
  }
}

/** Accepts a project URL such as https://abcd.supabase.co and drops any path. */
export function projectUrl(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return '';
    return parsed.origin;
  } catch {
    return '';
  }
}

export function isSupabaseConfigured(config: SupabaseConfig): boolean {
  return config.url.length > 0 && config.publishableKey.length > 0;
}

/** A saved pair wins. Otherwise the build-time default is used. */
export function resolveSupabaseConfig(stored: SupabaseConfig, env: SupabaseConfig): SupabaseConfig {
  const saved = sanitizeSupabaseConfig(stored);
  if (isSupabaseConfigured(saved)) return saved;
  return sanitizeSupabaseConfig(env);
}

export function loadStoredSupabase(): SupabaseConfig {
  try {
    if (typeof localStorage === 'undefined') return EMPTY_SUPABASE;
    const text = localStorage.getItem(STORAGE_KEY);
    if (!text) return EMPTY_SUPABASE;
    return sanitizeSupabaseConfig(JSON.parse(text) as unknown);
  } catch {
    return EMPTY_SUPABASE;
  }
}

export function saveStoredSupabase(config: SupabaseConfig): void {
  try {
    if (typeof localStorage === 'undefined') return;
    const clean = sanitizeSupabaseConfig(config);
    if (!isSupabaseConfigured(clean)) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(clean));
  } catch {
    /* private mode or a full disk */
  }
}
